import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { PropBot } from '@/types/prop-bots';
import { Loader2, Languages, Sparkles, Save, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { usePropBotI18nContent, usePropBotLanguageStatus, useSavePropBotI18nContent } from '@/hooks/use-prop-bot-i18n';

// Supported languages
const SUPPORTED_LANGUAGES = {
  'en': 'English',
  'vi': 'Tiếng Việt', 
  'zh': '中文',
  'ja': '日本語',
  'ko': '한국어',
  'es': 'Español'
};

// Schema for form validation
const propBotSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  status: z.string().min(1, 'Status is required'),
  color_scheme: z.string().min(1, 'Color scheme is required'),
  prop_firm: z.string().min(1, 'Prop firm name is required'),
  min_capital: z.coerce.number().min(1, 'Minimum capital is required'),
  max_drawdown: z.string().min(1, 'Maximum drawdown is required'),
  pass_rate: z.string().min(1, 'Pass rate is required'),
  estimated_pass_time: z.string().min(1, 'Estimated pass time is required'),
  challenge_duration: z.string().min(1, 'Challenge duration is required'),
  potential_profit: z.string().min(1, 'Potential profit is required'),
  winrate: z.string().min(1, 'Winrate is required'),
  risk: z.string().min(1, 'Risk is required'),
  trading_style: z.string().min(1, 'Trading style is required'),
  timeframe: z.string().min(1, 'Timeframe is required'),
  is_instant_account: z.boolean().default(false),
  // Updated array fields - keep them as strings in the form but transform when submitting
  account_sizes: z.string(),
  markets: z.string(),
  pairs: z.string(),
  features: z.string(),
  requirements: z.string(),
  challenge_rules: z.string(),
  original_language: z.string().optional(),
});

type PropBotFormValues = z.infer<typeof propBotSchema>;

interface PropBotFormProps {
  initialData?: PropBot;
  onSubmit: (data: any) => void;
  isLoading?: boolean;
  mode: 'add' | 'edit';
}

// Helper function to safely convert array or string to comma-separated string
const safeArrayToString = (value: any): string => {
  if (!value) return '';
  
  if (typeof value === 'string') {
    // Check if it's a JSON string
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }
    } catch (e) {
      // Not a valid JSON string, return as is
      return value;
    }
  }
  
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  
  return String(value);
};

export const PropBotForm = ({
  initialData,
  onSubmit,
  isLoading = false,
  mode,
}: PropBotFormProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(false);
  const [isRetranslating, setIsRetranslating] = useState(false);
  
  // Get multilingual content and language status
  const { data: i18nContent, refetch: refetchI18nContent } = usePropBotI18nContent(
    initialData?.id, 
    selectedLanguage
  );
  const { data: languageStatus } = usePropBotLanguageStatus(initialData?.id);
  const saveI18nContent = useSavePropBotI18nContent();
  
  console.log('PropBotForm initialData:', initialData);
  
  if (initialData?.propDetails) {
    console.log('PropDetails markets type:', typeof initialData.propDetails.markets);
    console.log('PropDetails markets value:', initialData.propDetails.markets);
  }
  
  // Initialize form with default values or existing data
  const form = useForm<PropBotFormValues>({
    resolver: zodResolver(propBotSchema),
    defaultValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      status: initialData?.status || 'draft',
      color_scheme: initialData?.color_scheme || 'ruby_red',
      prop_firm: initialData?.propDetails?.prop_firm || '',
      min_capital: initialData?.propDetails?.min_capital || 0,
      max_drawdown: initialData?.propDetails?.max_drawdown || '',
      pass_rate: initialData?.propDetails?.pass_rate || '',
      estimated_pass_time: initialData?.propDetails?.estimated_pass_time || '',
      challenge_duration: initialData?.propDetails?.challenge_duration || '',
      potential_profit: initialData?.propDetails?.potential_profit || '',
      winrate: initialData?.propDetails?.winrate || '',
      risk: initialData?.propDetails?.risk || '',
      trading_style: initialData?.propDetails?.trading_style || '',
      timeframe: initialData?.propDetails?.timeframe || '',
      is_instant_account: initialData?.propDetails?.is_instant_account || false,
      // Safely convert array fields to comma-separated strings
      account_sizes: safeArrayToString(initialData?.propDetails?.account_sizes),
      markets: safeArrayToString(initialData?.propDetails?.markets),
      pairs: safeArrayToString(initialData?.propDetails?.pairs),
      features: safeArrayToString(initialData?.propDetails?.features),
      requirements: safeArrayToString(initialData?.propDetails?.requirements),
      challenge_rules: safeArrayToString(initialData?.propDetails?.challenge_rules),
    },
  });

  // Load content for selected language
  useEffect(() => {
    if (i18nContent && mode === 'edit') {
      setIsLoadingLanguage(true);
      // Update form with i18n content
      form.setValue('description', i18nContent.description || '');
      form.setValue('features', safeArrayToString(i18nContent.features));
      form.setValue('requirements', safeArrayToString(i18nContent.requirements));
      form.setValue('challenge_rules', safeArrayToString(i18nContent.challenge_rules));
      setIsLoadingLanguage(false);
    } else if (!i18nContent && selectedLanguage === 'en' && initialData) {
      // Fallback to original data for English
      form.setValue('description', initialData.description || '');
      form.setValue('features', safeArrayToString(initialData.propDetails?.features));
      form.setValue('requirements', safeArrayToString(initialData.propDetails?.requirements));
      form.setValue('challenge_rules', safeArrayToString(initialData.propDetails?.challenge_rules));
    }
  }, [i18nContent, selectedLanguage, mode, initialData, form]);

  const handleSubmit = (values: PropBotFormValues) => {
    // Transform form data to match PropBot structure with arrays properly converted
    const botData = {
      name: values.name,
      description: values.description,
      status: values.status,
      color_scheme: values.color_scheme,
      propDetails: {
        prop_firm: values.prop_firm,
        min_capital: Number(values.min_capital),
        max_drawdown: values.max_drawdown,
        pass_rate: values.pass_rate,
        estimated_pass_time: values.estimated_pass_time,
        challenge_duration: values.challenge_duration,
        potential_profit: values.potential_profit,
        winrate: values.winrate,
        risk: values.risk,
        trading_style: values.trading_style,
        timeframe: values.timeframe,
        is_instant_account: values.is_instant_account,
      // Convert comma-separated strings back to arrays
      account_sizes: values.account_sizes.split(',').map(item => item.trim()).filter(item => item !== ''),
      markets: values.markets.split(',').map(item => item.trim()).filter(item => item !== ''),
      pairs: values.pairs.split(',').map(item => item.trim()).filter(item => item !== ''),
      features: values.features.split(',').map(item => item.trim()).filter(item => item !== ''),
      requirements: values.requirements.split(',').map(item => item.trim()).filter(item => item !== ''),
      challenge_rules: values.challenge_rules.split(',').map(item => item.trim()).filter(item => item !== ''),
    },
    originalLanguage: values.original_language || selectedLanguage,
  };

  onSubmit(botData);
};

const handleAutoTranslate = async (forceRetranslate = false, specificLanguages?: string[]) => {
  if (!initialData?.id) {
    toast.error('Please save the bot first before translating');
    return;
  }
  
  if (forceRetranslate || specificLanguages) {
    setIsRetranslating(true);
  } else {
    setIsTranslating(true);
  }

  try {
    const response = await supabase.functions.invoke('translate-prop-bot-content', {
      body: {
        botId: initialData.id,
        forceRetranslate,
        specificLanguages
      }
    });

    if (response.error) {
      throw new Error(response.error.message || 'Translation failed');
    }

    const result = response.data;
    if (!result.success) {
      throw new Error(result.error || 'Translation failed');
    }

    // Show detailed success message
    const { translatedLanguages = [], skippedLanguages = [] } = result;
    let message = forceRetranslate ? 'Re-translation completed!' : 'Auto-translation completed!';
    if (translatedLanguages.length > 0) {
      message += ` Updated: ${translatedLanguages.join(', ')}.`;
    }
    if (skippedLanguages.length > 0) {
      message += ` Skipped: ${skippedLanguages.join(', ')}.`;
    }
    
    toast.success(message);
    
    // Refresh current language content
    setTimeout(() => {
      refetchI18nContent();
    }, 1000);
    
    // If we're on original language tab and there are translated languages, suggest switching
    if (translatedLanguages.length > 0 && skippedLanguages.includes(selectedLanguage)) {
      setTimeout(() => {
        toast.info(`Switch to ${translatedLanguages[0]} tab to see the translated content`);
      }, 2000);
    }
    
  } catch (error: any) {
    console.error('Translation error:', error);
    toast.error(error.message || 'Translation failed. Please try again.');
  } finally {
    setIsTranslating(false);
    setIsRetranslating(false);
  }
};

const handleRetranslateLanguage = () => {
  handleAutoTranslate(false, [selectedLanguage]);
};

const handleSaveTranslation = async () => {
  if (!initialData?.id) {
    toast.error('Please save the bot first before saving translations');
    return;
  }

  const values = form.getValues();
  
  const i18nContent = {
    bot_id: initialData.id,
    language_code: selectedLanguage,
    description: values.description,
    features: values.features ? values.features.split(',').map(item => item.trim()).filter(item => item !== '') : [],
    requirements: values.requirements ? values.requirements.split(',').map(item => item.trim()).filter(item => item !== '') : [],
    challenge_rules: values.challenge_rules ? values.challenge_rules.split(',').map(item => item.trim()).filter(item => item !== '') : [],
    is_original_language: selectedLanguage === 'en', // Assume English is default
    is_ai_translated: false, // Manual edit
  };

  try {
    await saveI18nContent.mutateAsync(i18nContent);
  } catch (error) {
    console.error('Error saving translation:', error);
  }
};

const getLanguageBadge = (langCode: string) => {
  const status = languageStatus?.[langCode as keyof typeof languageStatus] as any;
  
  if (!status?.available) {
    return <Badge variant="outline" className="ml-2 text-xs">No content</Badge>;
  }
  
  if (status.isOriginal) {
    return <Badge variant="default" className="ml-2 text-xs">Original</Badge>;
  }
  
  if (status.isAiTranslated) {
    return <Badge variant="secondary" className="ml-2 text-xs">AI Translated</Badge>;
  }
  
  return <Badge className="ml-2 text-xs">Manual</Badge>;
};

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Basic Information
              {isLoadingLanguage && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading content...
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Language Tabs */}
            <Tabs value={selectedLanguage} onValueChange={setSelectedLanguage} className="w-full">
              <div className="flex items-center justify-between mb-4">
                <TabsList className="grid w-full grid-cols-6">
                  {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
                    <TabsTrigger key={code} value={code} className="text-xs">
                      {name.split(' ')[0]}
                      {getLanguageBadge(code)}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <div className="flex items-center gap-2 ml-4">
                  {mode === 'edit' && selectedLanguage !== 'en' && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSaveTranslation}
                        disabled={saveI18nContent.isPending}
                        className="flex items-center gap-1"
                      >
                        {saveI18nContent.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Save Translation
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRetranslateLanguage}
                        disabled={isRetranslating}
                        className="flex items-center gap-1"
                      >
                        {isRetranslating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        Re-translate
                      </Button>
                    </>
                  )}
                  {mode === 'edit' && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAutoTranslate()}
                        disabled={isTranslating}
                        className="flex items-center gap-1"
                      >
                        {isTranslating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4" />
                        )}
                        Auto-translate
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => handleAutoTranslate(true)}
                        disabled={isRetranslating}
                        className="flex items-center gap-1"
                      >
                        {isRetranslating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        Re-translate All
                      </Button>
                    </>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => refetchI18nContent()}
                    className="flex items-center gap-1"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Tabs>

            {selectedLanguage !== 'en' && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Editing:</strong> {SUPPORTED_LANGUAGES[selectedLanguage]}. 
                  Changes will be saved as manual translation. Use Auto-translate to generate AI translations for missing languages.
                </p>
              </div>
            )}
            
            <FormField
              control={form.control}
              name="original_language"
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormControl>
                    <Input {...field} value={selectedLanguage} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bot Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter bot name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter bot description" 
                      className="min-h-[100px]" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="color_scheme"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color Scheme</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select color scheme" />
                        </SelectTrigger>
                      </FormControl>
                       <SelectContent>
                         <SelectItem value="ruby_red">Ruby Red</SelectItem>
                         <SelectItem value="ocean_blue">Ocean Blue</SelectItem>
                         <SelectItem value="emerald_green">Emerald Green</SelectItem>
                         <SelectItem value="amber_gold">Amber Gold</SelectItem>
                         <SelectItem value="royal_purple">Royal Purple</SelectItem>
                         <SelectItem value="midnight_black">Midnight Black</SelectItem>
                         <SelectItem value="coral_orange">Coral Orange</SelectItem>
                       </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Prop Firm Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="prop_firm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prop Firm</FormLabel>
                    <FormControl>
                      <Input placeholder="FTMO, MyForexFunds, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="min_capital"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Minimum Capital ($)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="5000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="is_instant_account"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-amber-700 dark:text-amber-300 font-semibold">
                      Instant Account Prop Bot
                    </FormLabel>
                    <FormDescription className="text-amber-600 dark:text-amber-400">
                      Enable this for instant account prop bots that require live accounts only. 
                      Demo accounts will be restricted from connecting to this bot.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <FormField
                control={form.control}
                name="max_drawdown"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum Drawdown</FormLabel>
                    <FormControl>
                      <Input placeholder="5%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="pass_rate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pass Rate</FormLabel>
                    <FormControl>
                      <Input placeholder="92%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="estimated_pass_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estimated Pass Time</FormLabel>
                    <FormControl>
                      <Input placeholder="7-10 days" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="challenge_duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Challenge Duration</FormLabel>
                    <FormControl>
                      <Input placeholder="30 days" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="potential_profit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Potential Profit</FormLabel>
                    <FormControl>
                      <Input placeholder="Up to 90%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="winrate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Winrate</FormLabel>
                    <FormControl>
                      <Input placeholder="75%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="risk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Risk</FormLabel>
                    <FormControl>
                      <Input placeholder="Low" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="trading_style"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trading Style</FormLabel>
                    <FormControl>
                      <Input placeholder="Swing trading" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="timeframe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Timeframe</FormLabel>
                    <FormControl>
                      <Input placeholder="H1, H4, D1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lists and Requirements (Multilingual Content)</CardTitle>
            <FormDescription>
              These fields will be translated automatically to all supported languages when you use Auto-translate.
            </FormDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="account_sizes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Sizes</FormLabel>
                  <FormControl>
                    <Input placeholder="10k, 25k, 50k, 100k (comma-separated)" {...field} />
                  </FormControl>
                  <FormDescription>Enter values separated by commas</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="markets"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Markets</FormLabel>
                  <FormControl>
                    <Input placeholder="Forex, Indices, Commodities (comma-separated)" {...field} />
                  </FormControl>
                  <FormDescription>Enter values separated by commas</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pairs"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trading Pairs</FormLabel>
                  <FormControl>
                    <Input placeholder="EUR/USD, GBP/JPY, etc. (comma-separated)" {...field} />
                  </FormControl>
                  <FormDescription>Enter values separated by commas</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="features"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Features</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Smart risk management, Advanced entry algorithm, etc. (comma-separated)" 
                      className="min-h-[100px]" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>Enter values separated by commas</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="requirements"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Requirements</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="MetaTrader 4, 24/7 VPS, etc. (comma-separated)" 
                      className="min-h-[100px]" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>Enter values separated by commas</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="challenge_rules"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Challenge Rules</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="10% profit target, Max 5% drawdown, etc. (comma-separated)" 
                      className="min-h-[100px]" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>Enter values separated by commas</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {mode === 'add' ? 'Creating...' : 'Updating...'}
              </>
            ) : (
              mode === 'add' ? 'Create Bot' : 'Update Bot'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
